import { inject } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';

import { filter, map, switchMap, take } from 'rxjs';

/**
 * Redirects unauthenticated users to /access-denied?reason=unauthenticated.
 *
 * Waits for isLoading$ to settle before reading isAuthenticated$ — isAuthenticated$ is a
 * BehaviorSubject that starts at `false` and only flips to `true` once the SDK's initial
 * checkSession() (silent refresh-token auth) completes. Reading it immediately would treat
 * a user with a valid persisted session as logged out.
 */
export const authGuard: CanActivateFn = () => {
  const auth0 = inject(AuthService);
  const router = inject(Router);

  return auth0.isLoading$.pipe(
    filter((isLoading) => !isLoading),
    take(1),
    switchMap(() => auth0.isAuthenticated$.pipe(take(1))),
    map((isAuthenticated) =>
      isAuthenticated
        ? true
        : router.createUrlTree(['/access-denied'], { queryParams: { reason: 'unauthenticated' } }),
    ),
  );
};
